import React, { MouseEventHandler, ReactNode, forwardRef } from 'react';
import { SIZES, COLOR_VARIANTS } from './const';
import cn from 'classnames';
import styles from './text-field.module.scss';
import noop from 'lodash/noop';

type Size = (typeof SIZES)[number];
type Color = (typeof COLOR_VARIANTS)[number];
type HtmlInput = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'onChange' | 'onMouseDown'
>;
export type ChangeInputEvent = React.ChangeEvent<HTMLInputElement>;
export type ChangeOptions = { event: ChangeInputEvent | null };

export type TextFieldSizeProps = {
  size?: Size;
  sizeOnlyMobile?: Size;
  sizeOnlyDesktop?: Size;
};

export type TextFieldProps = HtmlInput &
  TextFieldSizeProps & {
    colorVariant?: Color;
    hasError?: boolean;
    flexedStyle?: boolean;
    onInputClick?: MouseEventHandler<HTMLInputElement>;
    onMouseDown?: MouseEventHandler<HTMLDivElement>;
    onChange?: (value: string, options: ChangeOptions) => void;
    children?: ReactNode;
  };

const getSizeClasses = (
  size: Size,
  sizeOnlyMobile?: Size,
  sizeOnlyDesktop?: Size,
) => {
  const withoutSizePerDevice = !sizeOnlyMobile && !sizeOnlyDesktop;

  return [
    withoutSizePerDevice && styles[`default-size-${size}`],
    sizeOnlyMobile && styles[`mobile-size-${sizeOnlyMobile}`],
    sizeOnlyDesktop && styles[`desktop-size-${sizeOnlyDesktop}`],
  ];
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      name,
      hasError,
      children,
      className,
      disabled,
      size = 'small',
      colorVariant = 'grey',
      flexedStyle = true,
      onMouseDown = noop,
      onInputClick = noop,
      onChange = noop,
      sizeOnlyMobile,
      sizeOnlyDesktop,
      ...inputAtt
    },
    ref,
  ) => {
    return (
      <div
        data-testid={`text-field-wrapper-${name}`}
        className={cn(
          styles.box,
          flexedStyle && styles.flexed,
          styles[size],
          hasError && styles.hasError,
          disabled ? styles.disabled : styles[colorVariant],
          ...getSizeClasses(size, sizeOnlyMobile, sizeOnlyDesktop),
          className,
        )}
        onClick={onInputClick}
        onMouseDown={onMouseDown}
      >
        <input
          data-testid={`text-field-${name}`}
          {...inputAtt}
          className={styles.input}
          disabled={disabled}
          aria-disabled={disabled}
          name={name}
          onChange={(event) => onChange(event.target.value, { event })}
          ref={ref}
        />
        {children}
      </div>
    );
  },
);
